// @ts-nocheck
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth, useUser } from '@clerk/clerk-react';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { useLocalization, type Language } from '../../../config/localization';
import Icon, { ICONS } from '../../ui/icons';
import { useContextNavigation, ADMIN_NAVIGATION } from '../../../hooks/useContextNavigation';

interface AdminContextProps {
  selectedLanguage: Language;
  onLanguageChange: (language: Language) => void;
}

interface AdminStats {
  totalUsers: number;
  totalOrganizations: number;
  globalPrompts: number;
  activeUsers: number;
}

export default function AdminContext({
  selectedLanguage,
  onLanguageChange
}: AdminContextProps) {
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const { user } = useUser();
  const location = useLocation();
  const t = useLocalization(selectedLanguage);
  const { userAccess, isLoading: userLoading } = useOrganization();
  
  // Debug logging
  useEffect(() => {
    console.log('🔍 AdminContext state update:', {
      userLoading,
      userAccess: userAccess ? {
        userId: userAccess.userId,
        userEmail: userAccess.userEmail,
        isAdmin: userAccess.isAdmin,
        hasAccess: userAccess.hasAccess
      } : null,
      user: user ? { id: user.id, primaryEmailAddress: user.primaryEmailAddress?.emailAddress } : null,
      timestamp: new Date().toISOString()
    });
  }, [userLoading, userAccess, user]);
  
  // Use navigation hook for consistent behavior
  const { handleNavigation, getButtonStyles, activeItem } = useContextNavigation({
    contextKey: 'admin',
    navigationItems: ADMIN_NAVIGATION,
    defaultPath: '/admin/users'
  });
  if (typeof window !== 'undefined') {
    console.log('🧭 AdminContext state', {
      pathname: location.pathname,
      activeItem: activeItem?.id,
      activeItemPath: activeItem?.path
    });
  }
  
  const [stats, setStats] = useState<AdminStats>({
    totalUsers: 0,
    totalOrganizations: 0,
    globalPrompts: 0,
    activeUsers: 0
  });
  const [loading, setLoading] = useState(true);
  const [globalRole, setGlobalRole] = useState<string | null>(null);

  // Resolve the precise global role so root_admin-only nav items (Service Keys)
  // are hidden for plain admins. The page + backend also enforce this.
  useEffect(() => {
    if (!userAccess?.isAdmin) return;

    let cancelled = false;
    const loadRole = async () => {
      try {
        const token = await getToken({ template: 'withmail' });
        const response = await fetch('/api/admin/check-access', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          if (!cancelled) setGlobalRole(data.role || null);
        }
      } catch (error) {
        console.error('Error resolving global role:', error);
      }
    };

    loadRole();
    return () => { cancelled = true; };
  }, [userAccess?.isAdmin, getToken]);

  // Load admin statistics
  useEffect(() => {
    if (!userAccess?.isAdmin) return;

    const loadStats = async () => {
      try {
        const token = await getToken({ template: 'withmail' });
        
        // Load basic stats
        const [usersResponse, orgsResponse] = await Promise.all([
          fetch('/api/admin/users', {
            headers: { 'Authorization': `Bearer ${token}` }
          }),
          fetch('/api/admin/organizations', {
            headers: { 'Authorization': `Bearer ${token}` }
          })
        ]);

        const usersData = usersResponse.ok ? await usersResponse.json() : { users: [] };
        const orgsData = orgsResponse.ok ? await orgsResponse.json() : { organizations: [] };

        setStats({
          totalUsers: usersData.users?.length || 0,
          totalOrganizations: orgsData.organizations?.length || 0,
          globalPrompts: 4, // Placeholder
          activeUsers: 2 // Placeholder
        });
      } catch (error) {
        console.error('Error loading admin stats:', error);
      } finally {
        setLoading(false);
      }
    };

    loadStats();
  }, [userAccess?.isAdmin, getToken]);

  // Map ADMIN_NAVIGATION to include icons and localized labels.
  // root_admin-only items (Service Keys) are filtered out for plain admins.
  const adminSections = useMemo(() => ADMIN_NAVIGATION
    .filter(item => !item.rootAdminOnly || globalRole === 'root_admin')
    .map(item => ({
    ...item,
    icon: item.id === 'users' ? ICONS.USERS :
          item.id === 'organizations' ? ICONS.BUILDING :
          item.id === 'prompts' ? ICONS.PROMPTS :
          item.id === 'prompts-v2' ? ICONS.DOCUMENT :
          item.id === 'prompt-categories' ? ICONS.TAG :
          item.id === 'widgets' ? ICONS.SETTINGS :
          item.id === 'workflows' ? ICONS.ZAPS :
          item.id === 'debug' ? ICONS.SLIDERS :
          item.id === 'billing' ? ICONS.ZAPS :
          item.id === 'packages' ? ICONS.LAYERS :
          item.id === 'api-keys' ? ICONS.KEY :
          item.id === 'refunds' ? ICONS.WARNING : ICONS.ADMIN,
    label: t.adminContext?.[item.id] || item.label,
    description: t.adminContext?.[`${item.id}Desc`] || item.description,
    action: () => handleNavigation(item)
  })), [t.adminContext, handleNavigation, globalRole]);

  // Show loading state while user data is being fetched
  if (userLoading) {
    console.log('🔄 AdminContext: Showing loading state');
    return (
      <div className="h-full flex flex-col items-center justify-center p-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mb-4"></div>
        <h3 className="text-lg font-semibold text-white mb-2">
          {t.adminContext?.loadingAccess || 'Checking Access'}
        </h3>
        <p className="text-gray-400 text-center text-sm">
          {t.adminContext?.loadingAccessDesc || 'Verifying administrator privileges...'}
        </p>
      </div>
    );
  }

  // Show access denied only after user data is loaded and user is not admin
  if (!userAccess?.isAdmin) {
    console.log('❌ AdminContext: Access denied - user is not admin', {
      userAccess,
      isAdmin: userAccess?.isAdmin
    });
    return (
      <div className="h-full flex flex-col items-center justify-center p-4">
        <Icon name={ICONS.WARNING} size={48} className="text-red-400 mb-4" />
        <h3 className="text-lg font-semibold text-white mb-2">
          {t.adminContext?.accessDenied || 'Access Denied'}
        </h3>
        <p className="text-gray-400 text-center text-sm">
          {t.adminContext?.accessDeniedDesc || 'You do not have administrator privileges'}
        </p>
      </div>
    );
  }

  console.log('✅ AdminContext: Access granted - showing admin panel');

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-gray-700/50">
        <h3 className="text-lg font-semibold text-white mb-1 flex items-center gap-2">
          <Icon name={ICONS.ADMIN} size={20} className="text-purple-400" />
          {t.adminContext?.title || 'Admin Panel'}
        </h3>
        <p className="text-xs text-gray-400">
          {t.adminContext?.subtitle || 'System administration'}
        </p>
      </div>

      {/* Quick Stats */}
      <div className="p-4 border-b border-gray-700/50">
        <h4 className="text-sm font-medium text-gray-400 mb-3 uppercase tracking-wide">
          {t.adminContext?.quickStats || 'Quick Stats'}
        </h4>
        
        {loading ? (
          <div className="flex items-center justify-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-500"></div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-gray-800/50 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <Icon name={ICONS.USERS} size={16} className="text-blue-400" />
                <span className="text-xs text-gray-400">{t.adminContext?.users || 'Users'}</span>
              </div>
              <div className="text-lg font-bold text-white">{stats.totalUsers}</div>
            </div>
            
            <div className="bg-gray-800/50 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <Icon name={ICONS.BUILDING} size={16} className="text-green-400" />
                <span className="text-xs text-gray-400">{t.adminContext?.organizations || 'Orgs'}</span>
              </div>
              <div className="text-lg font-bold text-white">{stats.totalOrganizations}</div>
            </div>
            
            <div className="bg-gray-800/50 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <Icon name={ICONS.PROMPTS} size={16} className="text-purple-400" />
                <span className="text-xs text-gray-400">{t.adminContext?.prompts || 'Prompts'}</span>
              </div>
              <div className="text-lg font-bold text-white">{stats.globalPrompts}</div>
            </div>
            
            <div className="bg-gray-800/50 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <Icon name={ICONS.ZAPS} size={16} className="text-yellow-400" />
                <span className="text-xs text-gray-400">{t.adminContext?.active || 'Active'}</span>
              </div>
              <div className="text-lg font-bold text-white">{stats.activeUsers}</div>
            </div>
          </div>
        )}
      </div>

      {/* Admin Actions */}
      <div className="flex-1 p-4">
        <h4 className="text-sm font-medium text-gray-400 mb-3 uppercase tracking-wide">
          {t.adminContext?.administration || 'Administration'}
        </h4>
        <div className="space-y-2">
          {adminSections.map((section) => (
            <button
              key={section.id}
              onClick={() => {
                console.log('🖱️ Admin nav click', { id: section.id, to: section.path });
                section.action();
              }}
              className={getButtonStyles(section, "w-full flex items-center gap-3 p-3 rounded-lg group")}
            >
              <Icon 
                name={section.icon} 
                size={20} 
                className="text-current transition-colors" 
              />
              <div className="flex-1">
                <div className="text-sm font-medium text-current transition-colors">
                  {section.label}
                </div>
                <div className="text-xs text-gray-500 group-hover:text-gray-400 transition-colors">
                  {section.description}
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* System Status Footer */}
      <div className="p-4 border-t border-gray-700/50">
        <h4 className="text-sm font-medium text-gray-400 mb-3 uppercase tracking-wide">
          {t.adminContext?.systemStatus || 'System Status'}
        </h4>
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-400">API:</span>
            <span className="text-green-400 flex items-center gap-1">
              <div className="w-2 h-2 bg-green-400 rounded-full"></div>
              {t.adminContext?.operational || 'Operational'}
            </span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-400">Database:</span>
            <span className="text-green-400 flex items-center gap-1">
              <div className="w-2 h-2 bg-green-400 rounded-full"></div>
              {t.adminContext?.connected || 'Connected'}
            </span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-400">Storage:</span>
            <span className="text-green-400 flex items-center gap-1">
              <div className="w-2 h-2 bg-green-400 rounded-full"></div>
              {t.adminContext?.available || 'Available'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}